import {useCallback} from 'react';
import {useAuthorization} from './useAuthorization';
import SecurityAuditLogger, {SecurityEventType} from '../../../Services/audit/SecurityAuditLogger';

/**
 * useSecurityAudit - Lightweight hook for security audit logging
 * Only logs critical security events, integrates seamlessly with existing components
 */
export function useSecurityAudit () {
  const {user} = useAuthorization();
  const auditLogger = SecurityAuditLogger.getInstance();

  /**
   * Log unauthorized access attempt
   */
  const logUnauthorizedAccess = useCallback((
    resource: string,
    action: string,
    permission?: string
  ) => {
    auditLogger.logUnauthorizedAccess(user, resource, action, permission);
  }, [user, auditLogger]);

  /**
   * Log permission denied
   */
  const logPermissionDenied = useCallback((
    permission: string,
    resource?: string
  ) => {
    auditLogger.logPermissionDenied(user, permission, resource);
  }, [user, auditLogger]);

  /**
   * Log sensitive data access
   */
  const logSensitiveDataAccess = useCallback((
    resource: string,
    recordCount: number
  ) => {
    auditLogger.logSensitiveDataAccess(user, resource, recordCount);
  }, [user, auditLogger]);

  /**
   * Log export attempt
   */
  const logExportAttempt = useCallback((
    resource: string,
    recordCount: number,
    format: string
  ) => {
    auditLogger.logExportAttempt(user, resource, recordCount, format);
  }, [user, auditLogger]);

  /**
   * Log import attempt
   */
  const logImportAttempt = useCallback((
    resource: string,
    recordCount: number,
    format: string
  ) => {
    auditLogger.logImportAttempt(user, resource, recordCount, format);
  }, [user, auditLogger]);

  /**
   * Get recent security events
   */
  const getRecentEvents = useCallback((limit?: number) => {
    return auditLogger.getRecentEvents(limit);
  }, [auditLogger]);

  /**
   * Get security statistics
   */
  const getSecurityStats = useCallback(() => {
    return auditLogger.getSecurityStats();
  }, [auditLogger]);

  return {
    logUnauthorizedAccess,
    logPermissionDenied,
    logSensitiveDataAccess,
    logExportAttempt,
    logImportAttempt,
    getRecentEvents,
    getSecurityStats
  };
}
